import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { BarChart3, Phone, TrendingUp, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color = "primary" }) => (
  <Card className="bg-card border-border rounded-sm">
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
          color === "primary" ? "bg-primary/20" : "bg-secondary"
        }`}>
          <Icon size={24} className={color === "primary" ? "text-primary" : "text-muted-foreground"} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground font-['Chivo']">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const DailyLeadCount = () => {
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDailyData();
  }, []);

  const fetchDailyData = async () => {
    try {
      const response = await axios.get(`${API}/admin/daily-lead-count`);
      setDailyData(response.data);
    } catch (error) {
      console.error("Failed to fetch daily data:", error);
    } finally {
      setLoading(false);
    }
  };

  const totals = dailyData.reduce(
    (acc, item) => ({
      total_calls: acc.total_calls + item.total_calls,
      qualified_leads: acc.qualified_leads + item.qualified_leads,
      appointments_set: acc.appointments_set + item.appointments_set,
    }),
    { total_calls: 0, qualified_leads: 0, appointments_set: 0 }
  );

  const conversionRate = totals.total_calls > 0
    ? ((totals.qualified_leads / totals.total_calls) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-lead-count-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Chivo']">Daily Lead Count</h1>
        <p className="text-muted-foreground">Track daily calling performance</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Days Tracked"
          value={dailyData.length}
          icon={Calendar}
          color="secondary"
        />
        <StatCard
          title="Total Calls"
          value={totals.total_calls}
          icon={Phone}
          color="secondary"
        />
        <StatCard
          title="Qualified Leads"
          value={totals.qualified_leads}
          icon={TrendingUp}
          color="primary"
        />
        <StatCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          icon={BarChart3}
          color="primary"
        />
      </div>

      {/* Daily Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-['Chivo']">Daily Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold">
                    Date
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Total Calls
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Qualified Leads
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Appointments Set
                  </TableHead>
                  <TableHead className="bg-secondary/50 text-muted-foreground uppercase text-xs font-bold text-right">
                    Conversion Rate
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dailyData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No daily data available</p>
                      <p className="text-sm mt-1">Call logs will populate this table</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {dailyData.map((item) => {
                      const rate = item.total_calls > 0
                        ? ((item.qualified_leads / item.total_calls) * 100).toFixed(1)
                        : 0;
                      return (
                        <TableRow key={item.date} className="hover:bg-secondary/20">
                          <TableCell className="font-mono font-medium">
                            {item.date}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {item.total_calls}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            <span className="text-[hsl(var(--success))]">
                              {item.qualified_leads}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {item.appointments_set}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`inline-flex items-center px-2 py-1 text-xs rounded-sm font-mono ${
                              parseFloat(rate) >= 30
                                ? "badge-success"
                                : parseFloat(rate) >= 15
                                ? "badge-warning"
                                : "bg-secondary text-muted-foreground"
                            }`}>
                              {rate}%
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals Row */}
                    <TableRow className="bg-secondary/30 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right font-mono">
                        {totals.total_calls}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[hsl(var(--success))]">
                        {totals.qualified_leads}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {totals.appointments_set}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center px-2 py-1 text-xs rounded-sm badge-info font-mono">
                          {conversionRate}%
                        </span>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyLeadCount;
